import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useTenant } from '../../context/TenantContext';

const GROUPS = [
  {
    key: 'gestao',
    label: 'GESTÃO',
    items: [
      { to: '/dashboard', icon: '📊', label: 'Dashboard', modulo: 'dashboard' },
      { to: '/assets', icon: '🏷️', label: 'Ativos', modulo: 'ativos' },
      { to: '/instruments', icon: '🔧', label: 'Instrumentos', modulo: 'instrumentos' },
      { to: '/loops', icon: '🔗', label: 'Malhas', modulo: 'malhas' },
    ],
  },
  {
    key: 'calibracao',
    label: 'CALIBRAÇÃO',
    items: [
      { to: '/calibrations', icon: '📋', label: 'Calibração', modulo: 'calibracoes' },
      { to: '/padroes-referencia', icon: '📐', label: 'Padrões', modulo: 'padroes' },
      { to: '/verificacoes', icon: '✅', label: 'Verificações', modulo: 'verificacoes' },
      { to: '/orcamentos',  icon: '💰', label: 'Orçamentos',   modulo: 'orcamentos'   },
      { to: '/calendar', icon: '📅', label: 'Calendário', modulo: 'calendario' },
      { to: '/reports', icon: '📈', label: 'Relatórios', modulo: 'relatorios' },
    ],
  },
  {
    key: 'admin',
    label: 'ADMIN',
    supervisorOnly: true,
    items: [
      { to: '/classes', icon: '🗂️', label: 'Classes' },
      { to: '/usuarios', icon: '👥', label: 'Usuários' },
      { to: '/audit', icon: '🔎', label: 'Audit Trail' },
      { to: '/settings', icon: '⚙️', label: 'Configurações' },
    ],
  },
  {
    key: 'superadmin',
    label: 'SUPERADMIN',
    superadminOnly: true,
    items: [
      { to: '/admin/empresas', icon: '🏢', label: 'Empresas' },
    ],
  },
];

export default function Sidebar({ drawerOpen, onDrawerClose }) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { hasModule } = useTenant();
  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState({ gestao: false, calibracao: false, admin: false });

  function toggleGroup(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // Show labels when expanded on desktop OR when drawer is open on mobile
  const showLabels = expanded || drawerOpen;

  return (
    <>
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onDrawerClose}
        />
      )}

      <aside
        className={[
          'bg-dark-800 border-r border-dark-600 flex flex-col py-3 flex-shrink-0 overflow-hidden',
          // Mobile: fixed drawer slides from left below header
          'fixed top-12 bottom-0 left-0 w-52 z-50 transition-transform duration-200',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: static in flow, width toggles
          'lg:relative lg:top-auto lg:bottom-auto lg:left-auto lg:z-auto lg:translate-x-0 lg:transition-all',
          expanded ? 'lg:w-52' : 'lg:w-14',
        ].join(' ')}
      >
        {/* Header / Toggle */}
        <div className={`flex items-center mb-4 ${showLabels ? 'px-3' : 'justify-center'}`}>
          {showLabels && (
            <span className="text-accent-blue font-bold text-sm flex-1 whitespace-nowrap">CalibraAI</span>
          )}
          {/* Desktop toggle */}
          <button
            onClick={() => setExpanded(o => !o)}
            className="hidden lg:flex w-8 h-8 rounded-lg items-center justify-center text-dark-400 hover:bg-dark-700 hover:text-white transition-colors flex-shrink-0"
            title={expanded ? 'Fechar menu' : 'Abrir menu'}
          >
            <span className="text-xs">{expanded ? '◀' : '▶'}</span>
          </button>
          {/* Mobile close button */}
          <button
            onClick={onDrawerClose}
            className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center text-dark-400 hover:bg-dark-700 hover:text-white transition-colors flex-shrink-0"
          >
            <span className="text-base leading-none">×</span>
          </button>
        </div>

        {/* Groups */}
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden gap-1">
          {GROUPS.map((group, gi) => {
            if (group.supervisorOnly && user?.role !== 'supervisor') return null;
            if (group.superadminOnly && user?.email !== 'brunoruotulo@gmail.com') return null;

            const visibleItems = group.items.filter(item =>
              (item.modulo ? can(item.modulo, 'view') : true) &&
              (item.modulo ? hasModule(item.modulo) : true)
            );
            if (visibleItems.length === 0) return null;

            const isGroupCollapsed = collapsed[group.key];
            const showItems = showLabels ? !isGroupCollapsed : true;

            return (
              <div key={group.key}>
                {!showLabels && gi > 0 && <div className="border-t border-dark-700 mx-2 mb-1" />}

                {showLabels && (
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center px-3 py-1 text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    <span className="flex-1 text-left tracking-wider">{group.label}</span>
                    <span className="text-xs">{isGroupCollapsed ? '▸' : '▾'}</span>
                  </button>
                )}

                {showItems && (
                  <div className={`flex flex-col ${showLabels ? 'px-2' : 'items-center px-1'} gap-0.5`}>
                    {visibleItems.map(item => (
                      <NavItem
                        key={item.to}
                        to={item.to}
                        icon={item.icon}
                        label={item.label}
                        open={showLabels}
                        onClose={onDrawerClose}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}

function NavItem({ to, icon, label, open, onClose }) {
  return (
    <NavLink
      to={to}
      onClick={onClose}
      title={!open ? label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded-lg transition-colors ${
          open ? 'w-full px-2 py-1.5' : 'w-9 h-9 justify-center'
        } ${
          isActive
            ? 'bg-accent-blue-dark text-blue-100'
            : 'text-dark-400 hover:bg-dark-700 hover:text-white'
        }`
      }
    >
      <span className="text-sm flex-shrink-0">{icon}</span>
      {open && <span className="text-sm truncate">{label}</span>}
    </NavLink>
  );
}
